import { FC, FormEvent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, TextField, IconButton, Dialog, DialogActions, DialogContent, DialogTitle, Typography, OutlinedInput, FormControlLabel, Checkbox, Autocomplete, SlotProps, CheckboxInputSlotPropsOverrides, CheckboxOwnerState } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Brightness1Icon from "@mui/icons-material/Brightness1";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CheckIcon from "@mui/icons-material/Check";
import SaveIcon from "@mui/icons-material/Save";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import { useCreateWorkflowStatusLabelMutation, useUpdateWorkflowStatusLabelMutation } from "../../../../../../../../../shell/services/instance";
import { useFormDialogContext } from ".";
import { useDispatch } from "react-redux";
import { notify } from "../../../../../../../../../shell/store/notifications";
import { useGetUsersRolesQuery } from "../../../../../../../../../shell/services/accounts";
import { CreateStatusLabel, StatusLabel } from "../../../../../../../../../shell/services/types";
import { ColorMenu, colorMenu, RoleMenu } from "../../constants";
interface FormInputFieldWrapperProps {
  label: string;
  required?: boolean;
  description?: string;
  error?: string;
  children: ReactNode;
}
export type StatusLabelFormProps = {
  open: boolean;
  onClose: () => void;
  labels?: StatusLabel[];
  values?: StatusLabel | undefined;
  isDeactivated?: boolean;
};
const FormInputFieldWrapper: FC<FormInputFieldWrapperProps> = ({
  label,
  required = false,
  description,
  error,
  children
}) => <Box display="flex" flexDirection="column" pb={0.25} position="relative" data-cy="status-label-field-wrapper" data-sentry-element="Box" data-sentry-component="FormInputFieldWrapper" data-sentry-source-file="StatusLabelForm.tsx">
    <Typography variant="body2" fontWeight={600} data-sentry-element="Typography" data-sentry-source-file="StatusLabelForm.tsx">
      {label}
      {required && <InfoRoundedIcon color="action" sx={{
      height: "12px",
      aspectRatio: 1,
      mt: 0.15
    }} />}
    </Typography>
    {description && <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>}
    <Box pt={0.25} display="flex" flexDirection="column" justifyContent="flex-start" alignItems="stretch" data-sentry-element="Box" data-sentry-source-file="StatusLabelForm.tsx">
      {children}
    </Box>
    {!!error && <Typography data-cy="status-label-field-error" variant="caption" color="error" sx={{
    position: "absolute",
    bottom: 0,
    transform: "translateY(calc(100% - 4px))"
  }}>
        {error}
      </Typography>}
  </Box>;
const ColorSelectInput = ({
  id,
  name,
  defaultValue = "",
  usedColors = []
}: {
  id: string;
  name: string;
  defaultValue?: string | "";
  usedColors: string[];
}) => {
  const {
    t
  } = useTranslation();
  const availableColors = colorMenu.sort((a, b) => t(a.label).localeCompare(t(b.label))).filter(item => !usedColors.includes(item.value));
  const defaultColor = colorMenu?.find(item => item?.value?.trim()?.toUpperCase() === defaultValue?.trim()?.toUpperCase()) || availableColors?.[0] || colorMenu?.[0];
  const [selectedColor, setSelectedColor] = useState<ColorMenu>(defaultColor);
  return <>
      <Autocomplete disableClearable autoHighlight fullWidth options={colorMenu} size="small" onChange={(event, newValue) => setSelectedColor(newValue)} value={selectedColor} getOptionLabel={option => t(option.label)} renderOption={(props, option) => <li {...props}>
            <Box display="flex" alignItems="center" gap={1}>
              <Brightness1Icon sx={{
          color: option.value,
          width: "1.25rem",
          height: "1.25rem"
        }} />
              <Typography variant="body2" color="text.primary">
                {t(option.label)}
              </Typography>
            </Box>
          </li>} renderInput={params => <TextField {...params} InputProps={{
      ...params.InputProps,
      startAdornment: <Brightness1Icon sx={{
        ml: ".25rem",
        color: selectedColor.value,
        width: "1.15rem",
        height: "1.15rem"
      }} />
    }} inputProps={{
      ...params?.inputProps,
      "data-amp-track-id": id
    }} />} data-sentry-element="Autocomplete" data-sentry-source-file="StatusLabelForm.tsx" />
      <input type="hidden" name={name} value={selectedColor.value?.trim()?.toUpperCase()} />
    </>;
};
const RolesSelectInput = ({
  id,
  name,
  listData,
  defaultValue = ""
}: {
  id: string;
  name: string;
  listData: RoleMenu[];
  defaultValue?: string;
}) => {
  const {
    t
  } = useTranslation();
  const [value, setSelectedColor] = useState(defaultValue || "");
  const sortedListData = [...listData].sort((a, b) => a.label.localeCompare(b.label));
  const getRoleInfo = (zuids: string) => zuids ? sortedListData.filter(item => zuids.split(",").includes(item.value.trim())) : [];
  const handleChange = (_: unknown, newValue: RoleMenu[]) => setSelectedColor(newValue.map(item => item.value).join(","));
  return <>
      <Autocomplete multiple fullWidth options={sortedListData} disableCloseOnSelect value={getRoleInfo(value)} onChange={handleChange} size="small" getOptionLabel={option => option.label} renderOption={(props, option, {
      selected
    }) => <li {...props}>
            <Checkbox size="small" disableRipple icon={<CheckIcon fontSize="medium" sx={{
        opacity: 0
      }} />} checkedIcon={<CheckIcon fontSize="medium" />} checked={selected} value={option.value} sx={{
        marginRight: "5px",
        position: "absolute",
        left: 0
      }} />
            <Typography variant="body2" sx={{
        pl: 4
      }}>
              {option.label}
            </Typography>
          </li>} renderInput={params => <TextField {...params} inputProps={{
      ...params?.inputProps,
      "data-amp-track-id": id
    }} variant="outlined" placeholder={value ? "" : t("settings.none")} />} ChipProps={{
      sx: {
        height: "1.25rem",
        backgroundColor: "transparent",
        outline: "1px solid",
        outlineColor: theme => theme.palette.border
      }
    }} data-sentry-element="Autocomplete" data-sentry-source-file="StatusLabelForm.tsx" />
      <input type="hidden" name={name} value={value?.trim()} />
    </>;
};
const validateFormData = (formData: CreateStatusLabel, t: (key: string) => string) => {
  const errors: Record<string, string> = {};
  if (!formData.name) errors.name = t("settings.nameRequired");
  // if (!formData.description) errors.description = "Description is required";
  if (!formData.color) errors.color = t("settings.colorRequired");
  return errors;
};
const StatusLabelForm: FC<StatusLabelFormProps> = ({
  open,
  onClose,
  labels = [],
  values,
  isDeactivated = false
}) => {
  const {
    t
  } = useTranslation();
  const ZUID = values?.ZUID || undefined;
  const [rolesMenuItems, setRolesMenuItems] = useState<RoleMenu[]>([]);
  const {
    isLoading: rolesLoading,
    isFetching,
    data: rolesMenuData
  } = useGetUsersRolesQuery();
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const dispatch = useDispatch();
  const [createWorkflowStatusLabel, {
    isLoading: createLabelIsLoading
  }] = useCreateWorkflowStatusLabelMutation();
  const [updateWorkflowStatusLabel, {
    isLoading: editLabelIsLoading
  }] = useUpdateWorkflowStatusLabelMutation();
  const {
    openDeactivationDialog,
    setFocusedLabel
  } = useFormDialogContext();
  const usedColors = labels.map(label => label.color);
  const transformRoleValues = (value: string): string[] => value ? value.split(",") : [];
  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = Object.fromEntries(new FormData(e.currentTarget));
    const newStatusLabel: CreateStatusLabel = {
      name: formData.name as string,
      description: formData.description as string || "",
      color: formData.color as string,
      allowPublish: formData.allowPublish === "true",
      addPermissionRoles: transformRoleValues(formData.addPermissionRoles as string),
      removePermissionRoles: transformRoleValues(formData.removePermissionRoles as string)
    };
    const errors = validateFormData(newStatusLabel, t);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    } else {
      setFormErrors({});
    }
    try {
      const response: any = ZUID ? await updateWorkflowStatusLabel({
        ZUID,
        payload: newStatusLabel
      }) : await createWorkflowStatusLabel(newStatusLabel);
      if (response?.error) {
        throw new Error(response.error.data?.error || t("settings.errorOccurred"));
      }
      setFocusedLabel(response?.data?.ZUID);
    } catch (error) {
      dispatch(notify({
        kind: "error",
        message: t("settings.errorWithMessage", {
          message: error instanceof Error ? error.message : t("settings.unexpectedError")
        })
      }));
    } finally {
      onClose();
    }
  };
  const handleDeactivation = () => openDeactivationDialog({
    ZUID,
    name: values?.name,
    callBack: onClose
  });
  useEffect(() => {
    if (!open) {
      setFormErrors({});
    }
    if (rolesLoading || isFetching) return;
    const roles = rolesMenuData?.map(item => ({
      label: item?.role?.name as string,
      value: item?.role?.ZUID
    })) || [];
    const uniqueRoles = Array.from(new Map(roles.map(role => [role.value, role])).values());
    setRolesMenuItems(uniqueRoles);
  }, [rolesLoading, isFetching, rolesMenuData, open]);
  return <Dialog open={open} fullWidth maxWidth="sm" PaperProps={{
    component: "form",
    onSubmit: handleFormSubmit,
    sx: {
      borderRadius: 1
    }
  }} data-sentry-element="Dialog" data-sentry-component="StatusLabelForm" data-sentry-source-file="StatusLabelForm.tsx">
      <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="StatusLabelForm.tsx">
        <Box display="flex" alignItems="center" gap={1} data-sentry-element="Box" data-sentry-source-file="StatusLabelForm.tsx">
          {!!ZUID && <Brightness1Icon sx={{
          color: values?.color,
          width: "1.25rem",
          height: "1.25rem"
        }} />}
          <Typography variant="h5" fontWeight={700} flexGrow={1} data-sentry-element="Typography" data-sentry-source-file="StatusLabelForm.tsx">
            {values?.name ? t("settings.editStatusTitle", {
            name: values.name
          }) : t("settings.createStatus")}
          </Typography>
          <IconButton size="small" onClick={onClose} data-sentry-element="IconButton" data-sentry-source-file="StatusLabelForm.tsx">
            <CloseRoundedIcon fontSize="small" data-sentry-element="CloseRoundedIcon" data-sentry-source-file="StatusLabelForm.tsx" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{
      p: 0,
      backgroundColor: "grey.50"
    }} data-cy="status-label-form" data-sentry-element="DialogContent" data-sentry-source-file="StatusLabelForm.tsx">
        <Box display="flex" flexDirection="column" gap={3} p={2.5} data-sentry-element="Box" data-sentry-source-file="StatusLabelForm.tsx">
          <FormInputFieldWrapper label={t("common.name")} error={formErrors?.name} data-sentry-element="FormInputFieldWrapper" data-sentry-source-file="StatusLabelForm.tsx">
            <OutlinedInput name="name" defaultValue={values?.name || ""} placeholder={t("settings.statusNamePlaceholder")} size="small" inputProps={{
            "data-amp-track-id": "workflows-create-status-label-name-input"
          }} data-sentry-element="OutlinedInput" data-sentry-source-file="StatusLabelForm.tsx" />
          </FormInputFieldWrapper>
          <FormInputFieldWrapper label={t("settings.descriptionOptional")} description={t("settings.describeStatusDescription")} data-sentry-element="FormInputFieldWrapper" data-sentry-source-file="StatusLabelForm.tsx">
            <OutlinedInput name="description" defaultValue={values?.description || ""} multiline minRows={2} maxRows={2} placeholder={t("settings.statusDescriptionPlaceholder")} size="small" inputProps={{
            "data-amp-track-id": "workflows-create-status-label-description-input"
          }} data-sentry-element="OutlinedInput" data-sentry-source-file="StatusLabelForm.tsx" />
          </FormInputFieldWrapper>
          <FormInputFieldWrapper label={t("settings.color")} error={formErrors?.color} data-sentry-element="FormInputFieldWrapper" data-sentry-source-file="StatusLabelForm.tsx">
            <ColorSelectInput id="workflows-create-status-label-color-select" name="color" defaultValue={values?.color} usedColors={usedColors} data-sentry-element="ColorSelectInput" data-sentry-source-file="StatusLabelForm.tsx" />
          </FormInputFieldWrapper>
          <FormInputFieldWrapper label={t("settings.whichRolesCanAdd")} description={t("settings.addStatusNotification")} data-sentry-element="FormInputFieldWrapper" data-sentry-source-file="StatusLabelForm.tsx">
            <RolesSelectInput id="workflows-create-status-label-addPermissionRoles-select" name="addPermissionRoles" listData={rolesMenuItems} defaultValue={values?.addPermissionRoles?.join(",")} data-sentry-element="RolesSelectInput" data-sentry-source-file="StatusLabelForm.tsx" />
          </FormInputFieldWrapper>
          <FormInputFieldWrapper label={t("settings.whichRolesCanRemove")} description={t("settings.removeStatusNotification")} data-sentry-element="FormInputFieldWrapper" data-sentry-source-file="StatusLabelForm.tsx">
            <RolesSelectInput id="workflows-create-status-label-removePermissionRoles-select" name="removePermissionRoles" listData={rolesMenuItems} defaultValue={values?.removePermissionRoles?.join(",")} data-sentry-element="RolesSelectInput" data-sentry-source-file="StatusLabelForm.tsx" />
          </FormInputFieldWrapper>
          <FormControlLabel sx={{
          display: "flex",
          alignItems: "start"
        }} control={<Checkbox data-amp-track-id="workflows-create-status-label-allowPublish-toggle" name="allowPublish" defaultChecked={values?.allowPublish} disableRipple color="default" onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
          e.target.value = checked.toString();
        }} sx={{
          p: 0,
          ml: 1,
          color: "action.active",
          "&.Mui-checked": {
            color: "primary.main"
          }
        }} slotProps={{
          input: {
            "data-amp-track-id": "workflows-create-status-label-allowPublish-toggle"
          } as SlotProps<"input", CheckboxInputSlotPropsOverrides, CheckboxOwnerState>
        }} />} label={<Box pl={1}>
                <Typography variant="body2" color="text.primary" fontWeight={600}>
                  {t("settings.publishRequiresStatus")}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={400}>
                  {t("settings.publishRequiresStatusDescription")}
                </Typography>
              </Box>} data-sentry-element="FormControlLabel" data-sentry-source-file="StatusLabelForm.tsx" />
          {!!ZUID && !isDeactivated && <Box>
              <Button data-amp-track-id="workflows-create-status-label-deactivate-button" variant="outlined" color="inherit" onClick={handleDeactivation} startIcon={<PauseCircleOutlineRoundedIcon />} data-cy="form-deactivate-status-button">
                {t("settings.deactivateStatus")}
              </Button>
            </Box>}
        </Box>
      </DialogContent>
      <DialogActions sx={{
      pt: 2
    }} data-sentry-element="DialogActions" data-sentry-source-file="StatusLabelForm.tsx">
        <Button data-amp-track-id="workflows-create-status-label-cancel-button" onClick={onClose} variant="outlined" color="inherit" data-sentry-element="Button" data-sentry-source-file="StatusLabelForm.tsx">
          {t("common.cancel")}
        </Button>
        <Button data-amp-track-id="workflows-create-status-label-submit-button" data-cy="status-label-submit-button" type="submit" variant="contained" color="primary" loading={createLabelIsLoading || editLabelIsLoading} startIcon={<SaveIcon />} data-sentry-element="Button" data-sentry-source-file="StatusLabelForm.tsx">
          {ZUID ? t("common.save") : t("settings.createStatus")}
        </Button>
      </DialogActions>
    </Dialog>;
};
export default StatusLabelForm;